import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useLocations } from './useLocations';
import type { Location } from './useLocations';

export interface Job {
  id: number;
  description: string;
  location_lon: number;
  location_lat: number;
  location_id: number;
  setup: number;
  service: number;
  delivery: any[];
  pickup: any[];
  skills: any[];
  priority: number;
  time_windows: any[];
  project_id: number;
  created_at: string;
  updated_at: string;
  // Location master data from JOIN
  locations?: {
    id: number;
    name: string;
    address?: string;
    location_type: string;
  };
}

export interface JobCreate {
  description: string;
  location_id: number; // CRITICAL: Must reference master location
  // Coordinates will be auto-populated from location master
  location_lon?: number;
  location_lat?: number;
  setup?: number;
  service?: number;
  // Multi-dimensional arrays for delivery and pickup
  delivery?: number[];
  pickup?: number[];
  // Enhanced skills integration
  skills?: number[];
  // Enhanced priority system
  priority?: number;
  // Complex time windows
  time_windows?: Array<{
    start: number;
    end: number;
  }>;
  project_id: number;
  // Dataset context - REQUIRED for data association integrity
  scenario_id: number;  // REQUIRED: Must specify scenario context
  dataset_id: number;   // REQUIRED: Must specify dataset context
}

export interface JobUpdate {
  description?: string;
  location_id?: number; // Location master integration
  setup?: number;
  service?: number;
  delivery?: number[];
  pickup?: number[];
  skills?: number[];
  priority?: number;
  time_windows?: Array<{
    start: number;
    end: number;
  }>;
}

// Hook to get jobs for a project WITH LOCATION MASTER DEPENDENCY and scenario context
export const useJobs = (projectId: number, scenarioId?: number, options?: {
  search?: string;
  page?: number;
  limit?: number;
  dataset_name?: string;
  dataset_version?: number;
  dataset_id?: number;
  enabled?: boolean;
  include_all_project_data?: boolean;
}) => {
  // CRITICAL: Get locations first (master data dependency)
  const { data: locations, isLoading: locationsLoading } = useLocations(projectId, scenarioId, options);
  
  return useQuery({
    queryKey: ['jobs', projectId, scenarioId, options],
    queryFn: async () => {
      console.log('💼 [useJobs] Fetching jobs for project:', projectId, 'scenario:', scenarioId, 'options:', options);
      const result = await apiClient.getJobs(projectId, {
        ...options,
        scenario_id: scenarioId,
        dataset_name: options?.dataset_name,
        dataset_version: options?.dataset_version,
        dataset_id: options?.dataset_id,
        include_all_project_data: options?.include_all_project_data
      });
      console.log('🔍 [useJobs] Raw API response:', result);
      
      // Handle Edge Function response format: {success: true, data: [...], meta: {...}}
      if (result && result.success && Array.isArray(result.data)) {
        console.log('✅ [useJobs] Returning jobs array:', result.data.length);
        return result.data;
      }
      
      // Fallback: if it's already an array (direct response)
      if (Array.isArray(result)) {
        console.log('✅ [useJobs] Direct array response:', result.length);
        return result;
      }
      
      console.warn('⚠️ [useJobs] Unexpected response format, returning empty array');
      return [];
    },
    // DEPENDENT QUERY: Wait for projectId AND locations to be available
    enabled: (options?.enabled !== false && projectId > 0) && !!projectId && !locationsLoading && !!locations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
};

// Hook to get a single job WITH LOCATION DETAILS
export const useJob = (jobId: number, projectId: number) => {
  // Location dependency for single job
  const { data: locations, isLoading: locationsLoading } = useLocations(projectId);
  
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => apiClient.getJob(jobId, projectId),
    // Dependent query: wait for jobId, projectId, and locations
    enabled: !!jobId && !!projectId && !locationsLoading && !!locations,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get jobs WITH full location master data (comprehensive dependency)
export const useJobsWithLocations = (projectId: number, options?: {
  search?: string;
  page?: number;
  limit?: number;
  requireLocations?: boolean;
}) => {
  return useQuery({
    queryKey: ['jobsWithLocations', projectId, options],
    queryFn: () => apiClient.getJobsWithLocationDependency(projectId, options),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // renamed from cacheTime in React Query v5
  });
};

// Hook to get available locations for job creation (dropdown support) with context
export const useAvailableLocationsForJobs = (projectId: number, scenarioId?: number, options?: {
  dataset_name?: string;
  dataset_version?: number;
  dataset_id?: number;
}) => {
  const { data: locations, isLoading, error } = useLocations(projectId, scenarioId, { 
    limit: 100,
    dataset_name: options?.dataset_name,
    dataset_version: options?.dataset_version,
    dataset_id: options?.dataset_id
  });
  
  // Filter locations that have coordinates (required for jobs)
  const availableLocations = locations?.filter((location: Location) => 
    location.location_lon !== null && 
    location.location_lat !== null
  ) || [];
  
  return {
    data: availableLocations,
    isLoading,
    error,
    hasLocations: availableLocations.length > 0
  };
};

// Hook to create a job WITH LOCATION MASTER VALIDATION
export const useCreateJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (job: JobCreate) => {
      // Client-side validation for location master integration and context
      if (!job.location_id) {
        throw new Error('location_id is required - jobs must reference master locations');
      }
      if (!job.scenario_id) {
        throw new Error('scenario_id is required for job creation - ensure scenario context is provided');
      }
      if (!job.dataset_id) {
        throw new Error('dataset_id is required for job creation - ensure dataset context is provided');
      }
      if (!job.project_id) {
        throw new Error('project_id is required for job creation');
      }
      
      console.log('💼 Creating job with context:', {
        project_id: job.project_id,
        scenario_id: job.scenario_id,
        dataset_id: job.dataset_id,
        location_id: job.location_id
      });
      
      return apiClient.createJob(job);
    },
    onSuccess: (data, variables) => {
      // Handle Edge Function response format
      const jobData = data?.data || data;
      const jobId = jobData?.id;
      
      // Invalidate jobs list for the project and scenario
      queryClient.invalidateQueries({ queryKey: ['jobs', variables.project_id, variables.scenario_id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['jobsWithLocations', variables.project_id] });
      
      // Add the new job to the cache if we have a valid job ID
      if (jobId && jobData) {
        queryClient.setQueryData(['job', jobId], jobData);
      }
      
      // Log success for location master integration and context
      console.log('✅ Job created successfully with context:', {
        jobId: jobId || 'undefined',
        locationId: variables.location_id,
        projectId: variables.project_id,
        scenario_id: variables.scenario_id,
        dataset_id: variables.dataset_id,
        fullResponse: data
      });
    },
    onError: (error) => {
      console.error('❌ Job creation failed:', error);
    }
  });
};

// Hook to update a job WITH LOCATION MASTER SYNC
export const useUpdateJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectId, updates }: { 
      id: number; 
      projectId: number; 
      updates: JobUpdate 
    }) => {
      // If location_id is being updated, coordinates will be auto-synced from master location
      if (updates.location_id) {
        console.log('🔄 Updating job with new location - coordinates will be auto-synced:', {
          jobId: id,
          newLocationId: updates.location_id
        });
      }
      
      return apiClient.updateJob(id, projectId, updates);
    },
    onSuccess: (data, variables) => {
      // Update the job in cache
      queryClient.setQueryData(['job', variables.id], data);
      
      // Invalidate jobs list for the project
      queryClient.invalidateQueries({ queryKey: ['jobs', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobsWithLocations', variables.projectId] });
      
      // If location was updated, log the coordinate sync
      if (variables.updates.location_id) {
        console.log('✅ Job updated with location master sync:', {
          jobId: variables.id,
          newCoordinates: { lon: data.location_lon, lat: data.location_lat }
        });
      }
    },
    onError: (error) => {
      console.error('❌ Job update failed:', error);
    }
  });
};

// Hook to delete a job WITH DEPENDENCY CHECKS
export const useDeleteJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectId }: { id: number; projectId: number }) => 
      apiClient.deleteJob(id, projectId),
    onSuccess: (_, variables) => {
      // Remove job from cache
      queryClient.removeQueries({ queryKey: ['job', variables.id] });
      
      // Invalidate jobs list for the project
      queryClient.invalidateQueries({ queryKey: ['jobs', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobsWithLocations', variables.projectId] });
      
      console.log('✅ Job deleted successfully:', variables.id);
    },
    onError: (error) => {
      console.error('❌ Job deletion failed:', error);
    }
  });
};

// Hook to handle location master changes impact on jobs
export const useJobLocationMasterSync = (projectId: number) => {
  const queryClient = useQueryClient();
  
  // Listen for location changes and invalidate job queries
  const syncJobsWithLocationChanges = () => {
    queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    queryClient.invalidateQueries({ queryKey: ['jobsWithLocations', projectId] });
    
    console.log('🔄 Syncing jobs with location master changes for project:', projectId);
  };
  
  return {
    syncJobsWithLocationChanges,
    // Helper to check if any jobs are affected by location changes
    checkJobLocationImpact: async (locationId: number) => {
      const jobsData = queryClient.getQueryData(['jobs', projectId]) as Job[];
      const affectedJobs = jobsData?.filter(job => job.location_id === locationId) || [];
      
      return {
        hasImpact: affectedJobs.length > 0,
        affectedJobCount: affectedJobs.length,
        affectedJobs
      };
    }
  };
};

// Hook for job validation with location master constraints
export const useJobValidation = () => {
  return {
    validateJobCreate: (job: JobCreate, availableLocations: Location[]) => {
      const errors: string[] = [];
      
      if (!job.description?.trim()) {
        errors.push('Job description is required');
      }
      
      if (!job.location_id) {
        errors.push('Location selection is required - jobs must reference master locations');
      } else {
        const locationExists = availableLocations.some(loc => loc.id === job.location_id);
        if (!locationExists) {
          errors.push('Selected location does not exist or is not available for jobs');
        }
      }
      
      if (job.priority && (job.priority < 1 || job.priority > 5)) {
        errors.push('Priority must be between 1 and 5');
      }
      
      // Validate time windows
      if (job.time_windows && Array.isArray(job.time_windows)) {
        for (const tw of job.time_windows) {
          if (tw.start !== undefined && tw.end !== undefined && tw.start >= tw.end) {
            errors.push('Time window start must be before end');
          }
        }
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  };
};
